import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import type { VoiceRememberOffer } from "./use-voice-remember-offer";

/** The quiet pill over a composer after a dictated word was corrected:
 *  "Remember <word> for voice?" with Remember and dismiss; after a failed
 *  Remember it says so in place, and Remember tries again. */
export function VoiceRememberPill({
  offer,
  className,
}: {
  offer: VoiceRememberOffer;
  className?: string;
}) {
  const t = useT(inputBarMessages);
  return (
    <div
      className={cn(
        "flex w-fit max-w-full items-center gap-1.5 rounded-pill bg-glass-fill py-0.5 pl-3 pr-1 text-appkit-caption text-secondary shadow-glass-sm backdrop-blur-glass-sm motion-safe:animate-[ticker-fade-in_160ms_ease-out]",
        className,
      )}
      data-testid="voice-remember-pill"
    >
      {offer.error ? (
        <span
          role="alert"
          className="min-w-0 truncate font-medium text-danger"
          data-testid="voice-remember-error"
        >
          {offer.error}
        </span>
      ) : (
        <span className="min-w-0 truncate">
          {t.rich("rememberWord", {
            word: offer.word,
            b: (chunks) => (
              <b className="font-semibold text-primary">{chunks}</b>
            ),
          })}
        </span>
      )}
      <Button
        variant="secondary"
        size="sm"
        className="h-6 px-2.5"
        onClick={offer.onRemember}
        data-testid="voice-remember-accept"
      >
        {t("remember")}
      </Button>
      <Button
        variant="ghost"
        size="iconXs"
        className="h-6 w-6 hover:bg-[var(--color-interactive-hover)]"
        onClick={offer.onDismiss}
        aria-label={t("dismiss")}
        data-testid="voice-remember-dismiss"
      >
        <Icons.X className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  );
}
